/**
 * AAAprinters - User Designs Hook
 * Fetches and manages user's AI-generated designs
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';

export interface UserDesign {
  id: string;
  user_id: string;
  image_url: string;
  prompt: string | null;
  design_name: string | null;
  product_id: string | null;
  position_x: number | null;
  position_y: number | null;
  size_width: number | null;
  size_height: number | null;
  is_favorite: boolean;
  is_applied_to_product: boolean;
  created_at: string;
  updated_at: string;
}

interface UseUserDesignsReturn {
  designs: UserDesign[];
  loading: boolean;
  error: string | null;
  addDesign: (design: Omit<UserDesign, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<UserDesign>;
  updateDesign: (id: string, updates: Partial<UserDesign>) => Promise<void>;
  deleteDesign: (id: string) => Promise<void>;
  toggleFavorite: (id: string) => Promise<void>;
  refetch: () => Promise<void>;
  getDesignById: (designId: string) => UserDesign | undefined;
}

export function useUserDesigns(limit?: number): UseUserDesignsReturn {
  const { user } = useAuth();
  const [designs, setDesigns] = useState<UserDesign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDesigns = useCallback(async () => {
    if (!user) {
      setDesigns([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('user_designs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (limit) {
        query = query.limit(limit);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setDesigns(data || []);
    } catch (err) {
      console.error('Error fetching designs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch designs');
    } finally {
      setLoading(false);
    }
  }, [user, limit]);

  useEffect(() => {
    fetchDesigns();
  }, [fetchDesigns]);

  const addDesign = useCallback(
    async (design: Omit<UserDesign, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
      if (!user) throw new Error('User not authenticated');

      try {
        const { data, error: insertError } = await supabase
          .from('user_designs')
          .insert({
            user_id: user.id,
            ...design
          })
          .select()
          .single();

        if (insertError) throw insertError;

        setDesigns(prev => [data, ...prev]);
        return data;
      } catch (err) {
        console.error('Error adding design:', err);
        throw err;
      }
    },
    [user]
  );

  const updateDesign = useCallback(
    async (id: string, updates: Partial<UserDesign>) => {
      if (!user) throw new Error('User not authenticated');

      try {
        const { error: updateError } = await supabase
          .from('user_designs')
          .update(updates)
          .eq('id', id)
          .eq('user_id', user.id);

        if (updateError) throw updateError;

        setDesigns(prev =>
          prev.map(design => (design.id === id ? { ...design, ...updates } : design))
        );
      } catch (err) {
        console.error('Error updating design:', err);
        throw err;
      }
    },
    [user]
  );

  const deleteDesign = useCallback(
    async (id: string) => {
      if (!user) throw new Error('User not authenticated');

      try {
        const { error: deleteError } = await supabase
          .from('user_designs')
          .delete()
          .eq('id', id)
          .eq('user_id', user.id);

        if (deleteError) throw deleteError;

        setDesigns(prev => prev.filter(design => design.id !== id));
      } catch (err) {
        console.error('Error deleting design:', err);
        throw err;
      }
    },
    [user]
  );

  const toggleFavorite = useCallback(
    async (id: string) => {
      const design = designs.find(d => d.id === id);
      if (!design) return;

      await updateDesign(id, { is_favorite: !design.is_favorite });
    },
    [designs, updateDesign]
  );

  const getDesignById = useCallback(
    (designId: string) => {
      return designs.find(design => design.id === designId);
    },
    [designs]
  );

  return {
    designs,
    loading,
    error,
    addDesign,
    updateDesign,
    deleteDesign,
    toggleFavorite,
    refetch: fetchDesigns,
    getDesignById
  };
}
